import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./SanctumPaywall.module.scss"

/**
 * SanctumPaywall (`/sanctum`) — an original, self-contained ONBOARDING paywall
 * for Private Photo Vault Premium. Paywall type rolled at random for this run:
 * ONBOARDING.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority, and the whole concept is built to it. Security & privacy,
 * told not as a lock you open but as protection you BUILD: the innermost thing
 * you own — a memory — sits at the centre of a sanctum, and layer by layer your
 * real safeguards seat inward around it until the only way in is you.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Build the room only you can enter." Your most private memory rests in the
 * middle of the screen. Around it, five concentric rings — one per real
 * safeguard — settle inward one at a time, each closing another layer between
 * the world and the memory. Defence in depth, made visible: not a single door
 * or key, but layer upon layer, and every layer set by you. When the last ring
 * seats, the sanctum seals and the memory glows fully warm and alive — safe,
 * and yours alone.
 *
 * ── Divergence (vs. the 90+ prior concepts, incl. the security cluster) ─────
 * Deliberately far from its security neighbours (Static cipher-noise, Eyes Only
 * redaction, Safelight darkroom, Ward crest, Hallmark seal, OnlyYou keypad,
 * Vault Door, Keyholder, Tumbler dial, Perimeter geofence, Cipher, Decoy):
 * Layout      : RADIAL & CONCENTRIC — a single memory at the centre with five
 *               protective rings seating inward around it. Not a card list, a
 *               wall/mosaic, a dossier, a crest, a certificate, a door, a
 *               keypad or a console.
 * Interaction : ARM / SEAT-INWARD — the layers self-assemble on arrival, and
 *               each ring node is tappable to (re)seat that layer and read what
 *               it does. An ADDITIVE, build-your-protection model — not a slide-
 *               to-decrypt (Static), not a press-and-hold converge (Ward), not
 *               a redaction scan (Eyes Only), not a pull-cord (Safelight).
 * Metaphor    : DEFENCE IN DEPTH — concentric layers of protection wrapping the
 *               innermost memory, a sanctum built ring by ring.
 * Motion      : rings DRAWING closed and SEATING inward with a soft pulse toward
 *               the centre; warmth blooming as the seal completes — not a reveal
 *               of hidden pixels, not noise melting, not bars retracting.
 * Feature     : the real safeguards ARE the rings — each ring names and arms one
 *               protection; there is no separate bullet list bolted on.
 * Value frame : COMMITMENT + defence-in-depth + sole-access — you set every
 *               layer, and when it seals, the way in is yours alone.
 * Register    : calm, protective, warm — a sanctuary you build, not a threat you
 *               flee; reassuring, never fearful.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every safeguard named is a REAL Private Photo Vault feature (only-you lock
 * with Face ID / Touch ID / PIN · encrypted Cloud Vault · Decoy Vault · break-in
 * snapshots · app disguise). "Layers of protection" is an honest description of
 * genuinely stacked, independent safeguards. This is onboarding — nothing is at
 * stake yet, so there is NO data-loss threat, NO countdown, NO fabricated
 * scarcity. "Save 79%" is real arithmetic from live product data. Yearly is the
 * featured, default, most prominent price; monthly is small & secondary. The
 * 7-day free trial and billing terms read from real products. Renders on the
 * built-in debug data with bundled photos — no native injection, no external
 * egress.
 */

// The five real Private Photo Vault safeguards, ordered innermost → outermost.
// Each is one concentric ring seating around your memory.
const LAYERS = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Face ID, Touch ID, or a code only you know.",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "A private, encrypted backup only your key can open.",
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    name: "Decoy Vault",
    line: "A second code opens a harmless, empty vault.",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    line: "A silent photo of anyone who tries a wrong code.",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "Hide the whole vault behind an ordinary-looking app.",
  },
]

// The memories at the heart of the sanctum. Bundled, self-contained assets
// (curated from the repo). The centre slowly cycles so it reads as your whole
// library, not one photo.
const MEMORIES = [
  "/sanctum/beach.jpg",
  "/sanctum/dog.jpg",
  "/sanctum/hikers.jpg",
  "/sanctum/coffee.jpg",
  "/sanctum/m2.jpg",
]

// SVG geometry (viewBox 0 0 360 360, centre 180,180). Ring radii grow outward;
// each node sits on its own ring at a distinct angle so the guardians spiral
// rather than stack.
const CENTER = 180
const RADII = [84, 106, 128, 150, 172]
const ANGLES_DEG = [-90, -20, 46, 118, 192]

const ARM_STEP_MS = 380
const ARM_START_MS = 480
const MEMORY_CYCLE_MS = 2600

const SanctumPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── which layers are armed (seated) ───────────────────────────────────────
  const [armed, setArmed] = useState<boolean[]>(() => LAYERS.map(() => false))
  const [pulse, setPulse] = useState<{ i: number; k: number } | null>(null)
  const [focus, setFocus] = useState(0)
  const [memoryIndex, setMemoryIndex] = useState(0)
  const pulseKey = useRef(0)
  const timers = useRef<number[]>([])

  const armedCount = armed.filter(Boolean).length
  const sealed = armedCount === LAYERS.length
  const seal = armedCount / LAYERS.length

  const armLayer = useCallback((i: number) => {
    pulseKey.current += 1
    setPulse({ i, k: pulseKey.current })
    setFocus(i)
    setArmed((prev) => {
      if (prev[i]) return prev
      const next = prev.slice()
      next[i] = true
      return next
    })
  }, [])

  const clearTimers = useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }, [])

  // auto-seal on arrival so the payoff reads in the first glance
  const runSeal = useCallback(() => {
    clearTimers()
    setArmed(LAYERS.map(() => false))
    LAYERS.forEach((_, i) => {
      timers.current.push(
        window.setTimeout(() => armLayer(i), ARM_START_MS + i * ARM_STEP_MS),
      )
    })
  }, [armLayer, clearTimers])

  useEffect(() => {
    if (reduceMotion) {
      setArmed(LAYERS.map(() => true))
      setFocus(LAYERS.length - 1)
      return
    }
    runSeal()
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // slowly cycle the centre memory
  useEffect(() => {
    if (reduceMotion) return
    const id = window.setInterval(
      () => setMemoryIndex((m) => (m + 1) % MEMORIES.length),
      MEMORY_CYCLE_MS,
    )
    return () => window.clearInterval(id)
  }, [reduceMotion])

  const onNode = useCallback(
    (i: number) => {
      // arming an unseated layer seats it; tapping a seated one re-seats +
      // focuses it so its description reads.
      armLayer(i)
    },
    [armLayer],
  )

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const focused = LAYERS[focus]

  return (
    <div
      className={`${styles.root} ${sealed ? styles.isSealed : ""}`}
      style={{ ["--seal" as any]: seal }}
    >
      <div className={styles.warmth} aria-hidden="true" />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9 12.2l2.2 2.2L15 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <span className={styles.brandTag}>Premium</span>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={() => native.postNativeEvent("close")}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <section className={styles.intro}>
        <span className={styles.kicker}>
          <span className={styles.kickerDot} aria-hidden="true" />
          Layer by layer, yours alone
        </span>
        <h1 className={styles.title}>
          Build the room
          <br />
          only <span className={styles.titleAccent}>you</span> can enter.
        </h1>
      </section>

      {/* ── the sanctum ──────────────────────────────────────────────────── */}
      <section className={styles.stage}>
        <div className={styles.halo} aria-hidden="true" />

        <svg className={styles.rings} viewBox="0 0 360 360" aria-hidden="true">
          <defs>
            <radialGradient id="sanctumGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffd9a0" stopOpacity="0.55" />
              <stop offset="70%" stopColor="#f3b24a" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#f3b24a" stopOpacity="0" />
            </radialGradient>
          </defs>

          <circle
            cx={CENTER}
            cy={CENTER}
            r={196}
            fill="url(#sanctumGlow)"
            className={styles.centerGlow}
          />

          {RADII.map((r, i) => {
            const c = 2 * Math.PI * r
            const on = armed[i]
            return (
              <g key={i}>
                <circle
                  cx={CENTER}
                  cy={CENTER}
                  r={r}
                  className={styles.ringTrack}
                />
                <circle
                  cx={CENTER}
                  cy={CENTER}
                  r={r}
                  className={`${styles.ringArc} ${on ? styles.ringArcOn : ""}`}
                  style={{
                    strokeDasharray: c,
                    strokeDashoffset: on ? 0 : c,
                    ["--ci" as any]: `${i}`,
                  }}
                  transform={`rotate(-90 ${CENTER} ${CENTER})`}
                />
              </g>
            )
          })}
        </svg>

        {/* the innermost memory */}
        <div className={styles.core}>
          <div className={styles.coreRingPulse} key={pulse?.k ?? "none"} />
          {MEMORIES.map((src, i) => (
            <div
              key={src}
              className={`${styles.corePhoto} ${
                i === memoryIndex ? styles.corePhotoOn : ""
              }`}
              style={{ backgroundImage: `url(${src})` }}
            />
          ))}
          <div className={styles.coreVeil} />
          <div className={styles.coreSeal} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
              <path
                d="M9 12.2l2.2 2.2L15 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* the guardian nodes — one per ring / safeguard */}
        <div className={styles.nodes}>
          {LAYERS.map((layer, i) => {
            const rad = (ANGLES_DEG[i] * Math.PI) / 180
            const x = CENTER + RADII[i] * Math.cos(rad)
            const y = CENTER + RADII[i] * Math.sin(rad)
            const on = armed[i]
            return (
              <button
                key={layer.key}
                className={`${styles.node} ${on ? styles.nodeOn : ""} ${
                  focus === i ? styles.nodeFocus : ""
                }`}
                style={{
                  left: `${(x / 360) * 100}%`,
                  top: `${(y / 360) * 100}%`,
                }}
                onClick={() => onNode(i)}
                aria-label={`${layer.name}: ${layer.line} ${
                  on ? "Layer sealed." : "Tap to seal this layer."
                }`}
                aria-pressed={on}
              >
                <span className={styles.nodeDisc} aria-hidden="true">
                  <layer.Icon width={17} height={17} />
                </span>
              </button>
            )
          })}
        </div>
      </section>

      {/* ── readout: layers sealed + the focused safeguard's plain words ──── */}
      <div className={styles.readout}>
        <div className={styles.readTop}>
          <span className={styles.readState} aria-live="polite">
            {sealed ? "Sanctum sealed" : "Sealing your sanctum"}
          </span>
          <span className={styles.readCount}>
            <b>{armedCount}</b>
            <span>/ {LAYERS.length} layers</span>
          </span>
        </div>
        <div className={styles.focusLine} aria-live="polite">
          <span className={styles.focusDot} data-on={armed[focus]} />
          <span className={styles.focusName}>{focused.name}</span>
          <span className={styles.focusSep}>·</span>
          <span className={styles.focusText}>{focused.line}</span>
        </div>

        <ul className={styles.legend}>
          {LAYERS.map((layer, i) => {
            const on = armed[i]
            return (
              <li
                key={layer.key}
                className={`${styles.chip} ${on ? styles.chipOn : ""} ${
                  focus === i ? styles.chipFocus : ""
                }`}
              >
                <button onClick={() => onNode(i)} aria-label={layer.name}>
                  <span className={styles.chipIcon} aria-hidden="true">
                    <layer.Icon width={14} height={14} />
                  </span>
                  <span className={styles.chipName}>{layer.name}</span>
                </button>
              </li>
            )
          })}
        </ul>
      </div>

      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        Five independent safeguards, stacked around every photo and video — and
        the only way in is you.
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ──────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${
            plan === "yearly" ? styles.selected : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>Yearly · full sanctum</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            7 days free, then about {yearlyPerMonth}/mo billed yearly
          </span>
        </button>

        <button
          className={`${styles.monthly} ${
            plan === "monthly" ? styles.monthlySel : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Seal my sanctum — start free"
              : "Seal my sanctum — monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default SanctumPaywall
